// The message catalogue (decision B20): Norwegian bokmål by default, English switch. Strings by key; names are
// never translated. Each area lives in nb/<area>.ts and en/<area>.ts; docs/FILE-OWNERSHIP.md names the owner of each.
// nb.ts gathers the nb areas of P0 to P2 (and MessageKey), en.ts the same en areas, and all.ts holds every area of both
// languages, the eleven P4 areas too, with the synchronous t(). This file holds the helpers and imports no catalogue, so
// the web's shell carries neither language: each is a chunk of its own, and only the stored one loads before the first
// render (apps/web/src/lib/i18n.tsx, norsk.ts and english.ts; docs/CONTRACTS.md section 21.7). A P4 area loads with the
// screens that show it, through areaText() in i18n.tsx (docs/CONTRACTS.md sections 22.6 and 23.6).
import type { Locale } from '../layers.ts'

export type { MessageKey } from './nb.ts'

const INTL_LOCALE: Record<Locale, string> = { nb: 'nb-NO', en: 'en-GB' }
// 'min2' groups from five digits, so a year or another four-digit number reads as written ('1975', '12 000'), while
// formatNumber below groups from four ('1 500'): the two differ on purpose (docs/DECISIONS.md, P3b: R81).
const GROUPED: Partial<Record<Locale, Intl.NumberFormat>> = {}

/**
 * A message with its {placeholders} filled from vars; a placeholder vars does not name stays as it is. A number is
 * written for the locale ('12 000' in nb, '12,000' in en); a string is put in as it is.
 */
export function interpolate(locale: Locale, text: string, vars?: Record<string, string | number>): string {
  if (!vars) return text
  const fmt = (GROUPED[locale] ??= new Intl.NumberFormat(INTL_LOCALE[locale], { useGrouping: 'min2' }))
  return text.replace(/\{([A-Za-z0-9_]+)\}/g, (whole, name: string) => {
    const v = vars[name]
    return v === undefined ? whole : typeof v === 'number' ? fmt.format(v) : v
  })
}

/** The message `key` of a catalogue, interpolated: with vars.count 1 it is '<key>.one' when the catalogue has that key. */
export function translate(locale: Locale, catalogue: Readonly<Record<string, string>>, key: string, vars?: Record<string, string | number>): string {
  const one = vars?.count === 1 ? catalogue[`${key}.one`] : undefined
  return interpolate(locale, one ?? catalogue[key] ?? key, vars)
}

export function formatNumber(locale: Locale, n: number): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale]).format(n)
}

export function formatDate(locale: Locale, isoDate: string): string {
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], { dateStyle: 'medium', timeZone: 'Europe/Oslo' }).format(new Date(isoDate))
}
